import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../axiosInstance'; // custom instance
import { Download } from 'lucide-react';

export default function CommodityTable() {
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCommodity, setEditingCommodity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
  });
  const CATEGORY_OPTIONS = ['Pulses', 'Oilseeds', 'Other'];
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const token = localStorage.getItem('token');

  // Get user role from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userRole = user?.roles?.[0]?.name || '';
    setRole(userRole);
  }, []);

  // Fetch commodities
  const fetchCommodities = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await axios.get('commodity'); // ✅ no headers needed
      setCommodities(
        res.data.commodities ||
          res.data.data ||
          (Array.isArray(res.data) ? res.data : []),
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch commodities.');
    } finally {
      setLoading(false);
    }
  };

  // Add / Edit commodity
  const handleSave = async () => {
    setFormError('');
    if (!formData.name || !formData.code || !formData.category) {
      setFormError('All fields are required.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (editingCommodity) {
        await axios.patch(`commodity/${editingCommodity.id}`, {
          commodityname: formData.name,
          commoditycode: formData.code,
          category: formData.category,
        });
        setCommodities(
          commodities.map((c) =>
            c.id === editingCommodity.id
              ? { ...c, commodityname: formData.name, commoditycode: formData.code, category: formData.category }
              : c
          )
        );
      } else {
        const res = await axios.post('commodity', {
          commodityname: formData.name,
          commoditycode: formData.code,
          category: formData.category,
        });
        setCommodities([...commodities, res.data]);
      }

      setFormData({ name: '', code: '', category: '' });
      setFormError('');
      setEditingCommodity(null);
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save commodity.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle active status
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      setLoading(true);
      setError('');

      await axios.patch(`commodity/update-status/${id}`, {
        isActive: !currentStatus,
      });

      setCommodities(
        commodities.map((c) =>
          c.id === id ? { ...c, status: !currentStatus } : c,
        ),
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Build URL with filters
      let url = `commodity/excel/download?`;

      if (search) url += `name=${encodeURIComponent(search)}&`;

      // Remove trailing "&" or "?" if present
      url = url.replace(/[&?]$/, '');

      const res = await axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });

      // Create downloadable Excel blob with Unix timestamp
      const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `commodities_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export error:', err.response?.data || err.message);
      alert('❌ Failed to export commodity data');
    }
  };

  // Ref to track if initial data has been fetched
  const initialDataFetchedRef = useRef(false);

  // Fetch on mount - only once
  useEffect(() => {
    if (!initialDataFetchedRef.current) {
      fetchCommodities();
      initialDataFetchedRef.current = true;
    }
  }, []);

  // Reset form error when modal is closed
  useEffect(() => {
    if (!showModal) {
      setFormError('');
    }
  }, [showModal]);

  // Search & Pagination
  const filtered = commodities.filter((c) =>
    c.commodityname.toLowerCase().includes(search.toLowerCase()),
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900 overflow-hidden w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6 sm:mb-10 gap-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight text-center sm:text-left">
          📦 Commodity Management
        </h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {role === 'Admin' && (
            <button
              onClick={() => {
                setEditingCommodity(null);
                setFormData({ name: '', code: '', category: '' });
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg transition-all duration-200 font-semibold w-full sm:w-auto"
            >
              + Add New
            </button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg w-full sm:w-auto font-semibold"
          >
            <Download size={16} className="flex-shrink-0" />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="🔍 Search commodities..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-5 py-3 text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          ⚠️ {error}
        </div>
      )}

      {/* Table - Responsive */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white w-full"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                {['#', 'Name', 'Code', 'Category', 'Status', 'Actions'].map(
                  (head) => (
                    <th
                      key={head}
                      className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                    >
                      {head}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 sm:py-10">
                    ⏳ Loading...
                  </td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((item, idx) => (
                  <motion.tr
                    key={item.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {idx + 1 + (currentPage - 1) * itemsPerPage}
                    </td>
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">
                      {item.commodityname}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.commoditycode}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.category ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.category === 'Pulses'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {item.category}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.status
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      {/* Toggle + Edit - Admin only */}
                      {role === 'Admin' ? (
                        <div className="flex items-center gap-3">
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={item.status}
                              onChange={() =>
                                handleToggleStatus(item.id, item.status)
                              }
                              className="sr-only peer"
                            />
                            <div
                              className={`w-14 h-7 rounded-full relative transition-colors duration-300 ${
                                item.status ? 'bg-blue-600' : 'bg-red-600'
                              }`}
                            >
                              <span
                                className={`absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                                  item.status
                                    ? 'translate-x-7'
                                    : 'translate-x-0'
                                }`}
                              />
                            </div>
                          </label>
                          <button
                            onClick={() => {
                              setEditingCommodity(item);
                              setFormData({
                                name: item.commodityname,
                                code: item.commoditycode,
                                category: item.category || '',
                              });
                              setShowModal(true);
                            }}
                            className="text-xs px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold rounded-lg transition"
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-8 sm:py-10 text-gray-500 font-medium text-base sm:text-lg"
                  >
                    🚫 No commodities found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 sm:mt-8 text-base sm:text-lg">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="w-full sm:w-auto px-4 sm:px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium order-2 sm:order-1"
        >
          ⬅ Previous
        </button>
        <span className="text-gray-700 font-semibold order-1 sm:order-2">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(currentPage + 1)}
          className="w-full sm:w-auto px-4 sm:px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium order-3"
        >
          Next ➡
        </button>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-10 w-[90%] sm:w-[80%] md:w-[600px] max-h-[90vh] shadow-xl sm:shadow-2xl overflow-y-auto text-base sm:text-lg mx-4"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-5 sm:mb-8 text-blue-700">
                {editingCommodity ? '✏️ Edit Commodity' : '➕ Add Commodity'}
              </h2>

              {formError && (
                <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
                  ⚠️ {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-6">
                <input
                  type="text"
                  placeholder="Commodity Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="border p-4 rounded-lg text-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Commodity Code"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  className="border p-4 rounded-lg text-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="border p-4 rounded-lg text-lg shadow-sm focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                >
                  <option value="">Select Category</option>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6 sm:mt-10">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-gray-300 hover:bg-gray-400 font-semibold order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg disabled:opacity-50 order-1 sm:order-2"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
